import React from 'react';
import { PageHeader, Badge, Stat } from './shared';
import { ModalHeader, ModalShell } from './Modals';
import { Icon } from './ui/icons';

function timeAgo(timestamp: string): string {
  const now = new Date();
  const eventTime = new Date(timestamp);
  const diffMs = now.getTime() - eventTime.getTime();
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

function cx(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

interface Schema {
  id: string;
  name: string;
  color: string;
  description: string;
  fields: SchemaField[];
}

interface SchemaField {
  name: string;
  type: string;
  values?: string[];
  required: boolean;
  doc?: string;
}

interface Normalizer {
  id: string;
  analyzer: string;
  version: string;
  input: string;
  output: string;
  description: string;
  mapping: [string, string][];
  samples: number;
  status: 'active' | 'changed' | string;
  lastRun?: string;
  owner: string;
}

interface Interpreter {
  id: string;
  control: string;
  name: string;
  schema: string;
}

interface NormalizersData {
  schemas: Schema[];
  normalizers: Normalizer[];
}

interface Data {
  normalizers?: NormalizersData;
  interpreters?: { interpreters: Interpreter[] };
}

function SchemaCard({ 
  schema, 
  normalizers, 
  onClick 
}: { 
  schema: Schema; 
  normalizers: number; 
  onClick: () => void 
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-[10px] border border-[#F3E4C8] bg-white p-5 hover:border-ink/40 transition flex flex-col gap-3"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="h-10 w-10 rounded-[10px] grid place-items-center"
            style={{
              background: `${schema.color}1a`,
              border: `1px solid ${schema.color}33`,
            }}
          >
            <Icon name="package" size={18} stroke={schema.color} />
          </div>
          <div>
            <div className="font-display font-bold text-ink text-[16px]">{schema.name}</div>
            <div className="font-data text-[11px] text-ink/55 mt-0.5">{schema.id}</div>
          </div>
        </div>
        <Icon name="chevron-right" size={14} stroke="rgba(18,17,17,0.4)" />
      </div>
      <p className="font-display text-[13px] text-ink/70 line-clamp-2">{schema.description}</p>
      <div className="flex items-center gap-3 pt-3 border-t border-[#F7EDDB] font-data text-[12px]">
        <span className="text-ink/55">{schema.fields.length} fields</span>
        <span className="text-ink/30">·</span>
        <span className="text-ink/55">
          {normalizers} normalizer{normalizers !== 1 ? 's' : ''}
        </span>
      </div>
    </button>
  );
}

function SchemaDetailPanel({ 
  open, 
  schema, 
  normalizers, 
  interpreters, 
  onClose 
}: { 
  open: boolean; 
  schema: Schema | null; 
  normalizers: Normalizer[]; 
  interpreters: Interpreter[]; 
  onClose: () => void 
}) {
  if (!schema) return null;
  const adapters = normalizers.filter((n) => n.output === schema.id);
  const consumers = interpreters.filter((i) => i.schema === schema.id);
  return (
    <ModalShell open={open} onClose={onClose} width={760}>
      <ModalHeader
        title={schema.name}
        subtitle={`${schema.id} · canonical schema`}
        onClose={onClose}
        icon="package"
      />
      <div className="px-6 py-5 flex flex-col gap-5 overflow-y-auto">
        <p className="font-display text-[14px] text-ink/75">{schema.description}</p>

        <div>
          <div className="font-display font-bold text-ink text-[14px] mb-2">Fields</div>
          <div className="rounded-[10px] border border-[#F3E4C8] overflow-hidden">
            <div
              className="grid items-center px-4 h-9 border-b border-[#F7EDDB] bg-[#FCF8F1] font-display font-bold text-[11px] uppercase tracking-[0.04em] text-ink/55"
              style={{ gridTemplateColumns: '160px 110px 80px 1fr' }}
            >
              <span>Name</span>
              <span>Type</span>
              <span>Req.</span>
              <span>Notes</span>
            </div>
            {schema.fields.map((f) => (
              <div
                key={f.name}
                className="grid items-center px-4 py-2 border-t border-[#F7EDDB] first:border-0"
                style={{ gridTemplateColumns: '160px 110px 80px 1fr' }}
              >
                <span className="font-data text-[13px] font-bold text-ink">{f.name}</span>
                <span className="font-data text-[12px] text-ink/70">
                  {f.type}
                  {f.values ? ` (${f.values.join('|')})` : ''}
                </span>
                <span>
                  {f.required ? (
                    <Badge tone="info">required</Badge>
                  ) : (
                    <span className="font-data text-[12px] text-ink/30">—</span>
                  )}
                </span>
                <span className="font-data text-[12px] text-ink/65">{f.doc || ''}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-4">
            <div className="font-display font-bold text-ink text-[13px] mb-2">
              Filled by ({adapters.length})
            </div>
            <ul className="flex flex-col gap-1.5">
              {adapters.map((n) => (
                <li key={n.id} className="flex items-center gap-2 font-display text-[13px]">
                  <Icon name="package" size={12} stroke="#121111" />
                  <span className="font-bold text-ink">{n.analyzer}</span>
                  <span className="font-data text-[11px] text-ink/55">{n.version}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-4">
            <div className="font-display font-bold text-ink text-[13px] mb-2">
              Read by ({consumers.length})
            </div>
            <ul className="flex flex-col gap-1.5">
              {consumers.map((i) => (
                <li key={i.id} className="flex items-center gap-2 font-display text-[13px]">
                  <Icon name="git-branch" size={12} stroke="#121111" />
                  <span className="font-data text-[11px] text-ink/55">#{i.control}</span>
                  <span className="font-semibold text-ink truncate">{i.name}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

function NormalizerPanel({ 
  open, 
  n, 
  schemas, 
  onClose 
}: { 
  open: boolean; 
  n: Normalizer | null; 
  schemas: Schema[]; 
  onClose: () => void 
}) {
  if (!n) return null;
  const schema = schemas.find((s) => s.id === n.output);
  return (
    <ModalShell open={open} onClose={onClose} width={780}>
      <ModalHeader
        title={n.id}
        subtitle={`${n.analyzer} → ${n.output} · ${n.version}`}
        onClose={onClose}
        icon="package"
      />
      <div className="px-6 py-5 flex flex-col gap-5 overflow-y-auto">
        <p className="font-display text-[14px] text-ink/75">{n.description}</p>

        <div className="grid grid-cols-3 gap-3 rounded-[10px] border border-[#F3E4C8] bg-[#FCF8F1] p-4">
          <PipelineNode label="Input" value={n.input} icon="package" />
          <PipelineArrow label="normalize" />
          <PipelineNode label="Output" value={n.output} icon="shield" accent={schema?.color} />
        </div>

        <div>
          <div className="font-display font-bold text-ink text-[14px] mb-2">Field mapping</div>
          <div className="rounded-[10px] border border-[#F3E4C8] overflow-hidden">
            <div
              className="grid items-center px-4 h-9 border-b border-[#F7EDDB] bg-[#FCF8F1] font-display font-bold text-[11px] uppercase tracking-[0.04em] text-ink/55"
              style={{ gridTemplateColumns: '1.5fr 30px 1fr' }}
            >
              <span>{n.analyzer} path</span>
              <span></span>
              <span>Schema field</span>
            </div>
            {n.mapping.map(([from, to]) => (
              <div
                key={`${from}-${to}`}
                className="grid items-center px-4 py-2.5 border-t border-[#F7EDDB] first:border-0"
                style={{ gridTemplateColumns: '1.5fr 30px 1fr' }}
              >
                <span className="font-data text-[12px] text-ink/80">{from}</span>
                <Icon name="chevron-right" size={12} stroke="rgba(18,17,17,0.4)" />
                <span className="font-data text-[12px] font-bold text-ink">{to}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Stat label="Samples produced" value={n.samples.toLocaleString()} />
          <Stat
            label="Status"
            value={n.status === 'active' ? 'Active' : n.status === 'changed' ? 'Changed' : '—'}
            tone={n.status === 'active' ? 'ok' : 'warn'}
          />
          <Stat label="Last run" value={n.lastRun ? timeAgo(n.lastRun) : '—'} />
        </div>
      </div>
      <div className="px-6 py-4 border-t border-[#F7EDDB] flex justify-between items-center">
        <span className="font-data text-[12px] text-ink/55">Owned by {n.owner}</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-[8px] border border-[#F3E4C8] font-display font-semibold text-[14px] hover:bg-[#FCF8F1]"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() =>
              (window as any).notify?.('Mapping editor', {
                sub: 'Opening the field-mapping view…',
                tone: 'info',
              })
            }
            className="h-10 px-4 rounded-[8px] bg-[#FA500E] text-white font-display font-bold text-[14px] hover:opacity-90"
          >
            Edit mapping
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function PipelineNode({ 
  label, 
  value, 
  icon, 
  accent 
}: { 
  label: string; 
  value: string; 
  icon: string; 
  accent?: string 
}) {
  return (
    <div className="flex items-center gap-3">
      <div
        className="h-9 w-9 rounded-[8px] grid place-items-center bg-white border border-[#F3E4C8]"
        style={accent ? { borderColor: `${accent}55` } : undefined}
      >
        <Icon name={icon} size={14} stroke={accent || '#121111'} />
      </div>
      <div className="min-w-0">
        <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">
          {label}
        </div>
        <div className="font-display font-bold text-ink text-[13px] truncate">{value}</div>
      </div>
    </div>
  );
}

export function PipelineArrow({ label }: { label: string }) {
  return (
    <div className="grid place-items-center text-center">
      <Icon name="chevron-right" size={20} stroke="rgba(18,17,17,0.5)" />
      <span className="font-data text-[10px] text-ink/55 mt-1 uppercase tracking-[0.05em]">
        {label}
      </span>
    </div>
  );
}

export default function Normalizers({ data }: { data: Data }) {
  const [openedSchema, setOpenedSchema] = React.useState<Schema | null>(null);
  const [openedNorm, setOpenedNorm] = React.useState<Normalizer | null>(null);
  const [q, setQ] = React.useState('');
  const [filter, setFilter] = React.useState('all');

  if (!data?.normalizers) {
    return <div className="font-display text-ink/55">No normalizer data loaded.</div>;
  }
  const { schemas, normalizers } = data.normalizers;
  const interpreters = data.interpreters?.interpreters || [];

  const list = normalizers.filter((n) => {
    if (filter !== 'all' && n.output !== filter) return false;
    if (!q.trim()) return true;
    return (n.id + n.analyzer + n.input + n.output).toLowerCase().includes(q.toLowerCase());
  });

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Normalizers & schemas"
        subtitle="Tool-agnostic glue. Normalizers map analyzer output into canonical schemas so interpreters never depend on a specific tool's JSON shape."
        right={
          <button
            type="button"
            onClick={() => (window as any).openCreate?.('normalizer')}
            className="h-10 px-4 rounded-[10px] bg-[#FA500E] text-white font-display font-bold text-[15px] inline-flex items-center gap-2 hover:opacity-90 whitespace-nowrap shrink-0"
          >
            <Icon name="plus" size={16} stroke="#FFF" /> New normalizer
          </button>
        }
      />

      {/* Pipeline diagram */}
      <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-6">
        <div className="font-display font-bold text-[11px] uppercase tracking-[0.1em] text-ink/55 mb-4">
          The detection pipeline
        </div>
        <div
          className="grid items-center gap-1"
          style={{
            gridTemplateColumns:
              'minmax(0,1fr) 36px minmax(0,1fr) 36px minmax(0,1fr) 36px minmax(0,1fr)',
          }}
        >
          <PipelineStage title="Analyzer" sub="tool-specific" icon="package" />
          <PipelineArrow label="raw" />
          <PipelineStage title="Normalizer" sub="versioned adapter" icon="git-branch" />
          <PipelineArrow label="canonical" />
          <PipelineStage title="Interpreter" sub="schema-aware rule" icon="shield" />
          <PipelineArrow label="verdict" />
          <PipelineStage title="Control" sub="pass / fail" icon="check" accent="#FA500E" />
        </div>
      </div>

      {/* Canonical schemas */}
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-ink text-[16px]">Canonical schemas</div>
            <div className="font-data text-[12px] text-ink/55 mt-0.5">
              The shapes interpreters speak. Click a schema to view its fields.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {schemas.map((s) => (
            <SchemaCard
              key={s.id}
              schema={s}
              normalizers={normalizers.filter((n) => n.output === s.id).length}
              onClick={() => setOpenedSchema(s)}
            />
          ))}
        </div>
      </section>

      {/* Normalizers table */}
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-ink text-[16px]">Normalizers</div>
            <div className="font-data text-[12px] text-ink/55 mt-0.5">
              Each rule maps a specific analyzer's output to one canonical schema.
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 rounded-[4px] border border-[rgba(18,17,17,0.2)] px-5 h-11">
          <div className="flex items-center gap-3 flex-1">
            <Icon name="search" size={16} stroke="#121111" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Find a normalizer…"
              className="flex-1 bg-transparent outline-none font-display text-[14px] placeholder:text-ink/45"
            />
          </div>
          {q && (
            <button type="button" onClick={() => setQ('')}>
              <Icon name="x" size={16} stroke="#121111" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setFilter('all')}
            className={cx(
              'px-3 h-9 rounded-[8px] font-display text-[13px] inline-flex items-center gap-2',
              filter === 'all'
                ? 'bg-ink text-white font-semibold'
                : 'text-ink/70 hover:bg-[#F7EDDB]'
            )}
          >
            All{' '}
            <span className="font-data text-[11px] rounded-full px-1.5 min-w-[20px] text-center bg-white/15">
              {normalizers.length}
            </span>
          </button>
          {schemas.map((s) => (
            <button
              type="button"
              key={s.id}
              onClick={() => setFilter(s.id)}
              className={cx(
                'px-3 h-9 rounded-[8px] font-display text-[13px] inline-flex items-center gap-2',
                filter === s.id
                  ? 'bg-ink text-white font-semibold'
                  : 'text-ink/70 hover:bg-[#F7EDDB]'
              )}
            >
              <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
              {s.id}
            </button>
          ))}
        </div>

        <div className="rounded-[6px] border border-[#F3E4C8] bg-white overflow-hidden">
          <div
            className="grid items-center px-5 h-[54px] border-b border-[#F7EDDB] bg-[#FCF8F1] font-display font-bold text-[13px] text-ink"
            style={{ gridTemplateColumns: '1.4fr 1.2fr 1fr 1fr 1fr 90px' }}
          >
            <span>Normalizer</span>
            <span>From analyzer</span>
            <span>Maps to schema</span>
            <span>Owner</span>
            <span>Last run</span>
            <span>Status</span>
          </div>
          {list.map((n) => {
            const schema = schemas.find((s) => s.id === n.output);
            return (
              <button
                type="button"
                key={n.id}
                onClick={() => setOpenedNorm(n)}
                className="grid items-center px-5 py-4 border-t border-[#F7EDDB] first:border-0 hover:bg-[#FCF8F1] cursor-pointer text-left"
                style={{ gridTemplateColumns: '1.4fr 1.2fr 1fr 1fr 1fr 90px' }}
              >
                <div className="min-w-0">
                  <div className="font-display font-bold text-ink text-[14px] truncate">{n.id}</div>
                  <div className="font-data text-[11px] text-ink/55 mt-0.5">
                    {n.input} · {n.version}
                  </div>
                </div>
                <span className="inline-flex items-center gap-2 font-display text-[13px]">
                  <span className="h-7 w-7 rounded-[6px] grid place-items-center bg-[#FCF8F1] border border-[#F3E4C8]">
                    <Icon name="package" size={13} stroke="#121111" />
                  </span>
                  {n.analyzer}
                </span>
                <span className="inline-flex items-center gap-2 font-display text-[13px] font-semibold">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: schema?.color || '#999' }}
                  />
                  {n.output}
                </span>
                <span className="font-data text-[12px] text-ink/55">{n.owner}</span>
                <span className="font-data text-[12px] text-ink/55">
                  {n.lastRun ? timeAgo(n.lastRun) : '—'}
                </span>
                <Badge
                  tone={n.status === 'active' ? 'ok' : n.status === 'changed' ? 'warn' : 'muted'}
                  dot
                >
                  {n.status}
                </Badge>
              </button>
            );
          })}
        </div>
      </section>

      <SchemaDetailPanel
        open={!!openedSchema}
        schema={openedSchema}
        normalizers={normalizers}
        interpreters={interpreters}
        onClose={() => setOpenedSchema(null)}
      />
      <NormalizerPanel
        open={!!openedNorm}
        n={openedNorm}
        schemas={schemas}
        onClose={() => setOpenedNorm(null)}
      />
    </div>
  );
}

export function PipelineStage({ 
  title, 
  sub, 
  icon, 
  accent 
}: { 
  title: string; 
  sub: string; 
  icon: string; 
  accent?: string 
}) {
  return (
    <div className="rounded-[10px] border border-[#F3E4C8] bg-[#FCF8F1] p-4 flex items-center gap-3">
      <div
        className="h-10 w-10 rounded-[10px] grid place-items-center bg-white border border-[#F3E4C8]"
        style={accent ? { borderColor: `${accent}55`, background: `${accent}10` } : undefined}
      >
        <Icon name={icon} size={16} stroke={accent || '#121111'} />
      </div>
      <div className="min-w-0">
        <div className="font-display font-bold text-ink text-[14px] truncate">{title}</div>
        <div className="font-data text-[11px] text-ink/55 mt-0.5">{sub}</div>
      </div>
    </div>
  );
}